import { getSimpleForBuyRequest } from '@/js/helpers/buy-request'
import type { BuyRequest, IRogue, Product } from '@/js/services/RogueTypes'
import { Rogue } from '../Rogue'
import { IReflektionFacetValue, IReflektionProcessedProduct } from '../rfk/types'
import { QUOTED_PRODUCTS, PLACEHOLDER_PRODUCTS } from '../rfk/util'
import { IConstructorRawProduct, CnstrcContentPlacement, CnstrcContentSlot } from './types'

export const facetValueTextTransform: { [key: string]: (text: string) => string } = {
  promotions: (text) => {
    const promotionVals: { [key: string]: string } = {
      made_in_usa             : 'Made in USA',
      '3_ships_free'          : '3 Ships Free',
      rogue_closeout_and_sale : 'Closeout & Sale',
      hot_deal                : 'Hot Deals',
      black_friday_dropship   : '5 for 5',
      free_shipping           : 'Free Shipping',
      rogue_hundo_enabled     : 'Hundo',
    }

    return promotionVals[text] || text
  },
  is_salable: (text: string) => (`${text}` === '0' ? 'Out of Stock' : 'In Stock'),
}

/**
 * Builds the suggested-terms list shown under a search box. Autocomplete's own Search
 * Suggestions lead, and the search response's related searches top the list up when
 * autocomplete returns fewer than `limit`. The two APIs answer the same query and often
 * overlap, so terms are deduped case-insensitively with the earlier one winning.
 */
export function mergeSuggestedTerms<T extends { text: string }>(suggestions: T[], relatedSearches: T[], limit: number): T[] {
  const seen = new Set<string>()

  return [...suggestions, ...relatedSearches].reduce<T[]>((terms, term) => {
    const key = term?.text?.trim().toLowerCase()
    if (!key || seen.has(key) || terms.length >= limit) return terms

    seen.add(key)
    terms.push(term)

    return terms
  }, [])
}

// Generates a clean, consistent urlKey from the raw Constructor pb value.
// e.g. "-inf"-"50" → "Under $50.00", "50"-"100" → "$50.00 - $100.00", "500"-"inf" → "Over $500.00"
function formatPbUrlKey(rawValue: string): string {
  const match = rawValue.match(/^"(-inf|\d+)"-"(\d+|inf)"$/)
  if (!match) return rawValue

  const [, low, high] = match

  if (low === '-inf') return `Under $${high}`
  if (high === 'inf') return `Over $${low}`

  return `$${low} - $${high}`
}

// Reverses formatPbUrlKey back to the raw Constructor value for the API.
// Used when filters.value is not yet populated (initial SSR load with a pre-formatted URL).
export function decodePbFilterValue(urlKey: string): string {
  let match = urlKey.match(/^Under \$(\d+)$/)
  if (match) return `"-inf"-"${match[1]}"`

  match = urlKey.match(/^Over \$(\d+)$/)
  if (match) return `"${match[1]}"-"inf"`

  match = urlKey.match(/^\$(\d+) - \$(\d+)$/)
  if (match) return `"${match[1]}"-"${match[2]}"`

  return urlKey
}

export function processPriceRangeFacet(text: string, rogue: Rogue): string {
  let value = text
  const prices = value.match(/\$?((?:\d|,)*\.?\d+)/g) || []
  prices.forEach(price => value = value.replace(price, rogue.currency(price.replace('$', ''))))

  value = value.replace(/(.+)\s+and\s+less/i, 'Under $1')
  value = value.replace(/(.+)\s+and\s+more/i, 'Over $1')

  return value
}

type FacetModifyFn = (vals: IReflektionFacetValue[], rogue: Rogue) => IReflektionFacetValue[]

export const modifyFacetValues: { [key: string]: FacetModifyFn } = {
  is_salable     : (values: IReflektionFacetValue[]) => values.sort((el1, el2) => Number(el2.text) - Number(el1.text)),
  duration       : (values: IReflektionFacetValue[]) => values.sort((el1, el2) => parseInt(el1.text, 10) - parseInt(el2.text, 10)),
  days_per_week  : (values: IReflektionFacetValue[]) => values.sort((el1, el2) => parseInt(el1.text, 10) - parseInt(el2.text, 10)),
  rating_summary : () => {
    const retval: IReflektionFacetValue[] = []

    for (let stars = 5; stars > 1; stars -= 1) {
      let str = ''

      for (let icon = 1; icon < 6; icon += 1) {
        str += `<span ${icon < stars ? 'class="star gold"' : 'class="star"'}>★</span>`
      }

      if (stars < 6) {
        str += ' & Up'
      }

      retval.push({
        count  : 0,
        id     : `${stars}`,
        label  : str,
        text   : str,
        urlKey : `${stars - 1}`,
      })
    }

    return retval
  },
  pb: (values: IReflektionFacetValue[], rogue: Rogue) => values.map(range => {
    range.id = range.urlKey || range.text // preserve raw Constructor value for API calls
    range.text = processPriceRangeFacet(range.text, rogue)
    range.urlKey = formatPbUrlKey(range.id)

    return range
  }),
}

// Strips the Cloudinary base + transforms from both Constructor-style
// (assets.roguefitness.com/image/upload/<transforms>/<path>) and RFK-style
// (assets.roguefitness.com/<transforms>/<path>) URLs, leaving just /<path>.
const CLOUDINARY_PREFIX = /^https?:\/\/assets\.roguefitness\.com(?:\/image\/upload)?\/[^/]+\//

function extractThumbnailPath(imageUrl: string | undefined): string {
  if (!imageUrl) return '/catalog/product_placeholder.jpg'
  const match = imageUrl.match(CLOUDINARY_PREFIX)

  return match ? `/${imageUrl.slice(match[0].length)}` : imageUrl
}

// Strips domain from absolute Constructor URLs so rogue-link can apply the locale prefix (/es, /de, etc).
function toRelativeProductUrl(raw: string): string {
  if (!raw.startsWith('http')) return raw
  try {
    const { pathname, search } = new URL(raw)

    return pathname + search
  } catch {
    return raw
  }
}

export function processConstructorProduct(result: IConstructorRawProduct, rogue: Rogue): IReflektionProcessedProduct {
  const { data } = result
  const id = data.id || result.id
  const thumbnail = data.relative_image_url || extractThumbnailPath(data.image_url)
  const price = Number(data.price || 0)
  const salePrice = Number(data.special_price || 0)

  const manufacturer = Array.isArray(data.manufacturer) ? data.manufacturer : (data.manufacturer ? [data.manufacturer] : undefined)

  const processed: IReflektionProcessedProduct = {
    productId        : parseInt(id, 10),
    name             : data.name || result.value || '',
    url              : toRelativeProductUrl(data.relative_url || data.url || ''),
    thumbnail,
    price,
    sku              : data.sku != null ? String(data.sku) : '',
    type             : data.type || 'simple',
    categories       : (data.categories as string[] | undefined) || [],
    brand            : manufacturer?.[0],
    challenge_status : data.challenge_status,
    attributes       : {
      tax_class_id : data.tax_class_id || undefined,
      manufacturer : manufacturer?.join(','),
    },
  }

  if (salePrice && salePrice < price) {
    processed.special_price = salePrice
  }

  if (result.variation_id) {
    processed.variationKey = result.variation_id
  }

  processed.quoted = Boolean(QUOTED_PRODUCTS.includes(id) && (Number.isNaN(processed.price) || processed.price === 0))

  if (PLACEHOLDER_PRODUCTS[id]) {
    processed.productId = PLACEHOLDER_PRODUCTS[id].productId
    processed.url = PLACEHOLDER_PRODUCTS[id].url
  }

  processed.altImage = data.rogue_hover_image ? extractThumbnailPath(data.rogue_hover_image) : undefined

  if (data.rating_count != null) {
    processed.ratingCount = Number(data.rating_count)
    const rawRating = data.rating_summary_orig ?? data.rating_summary
    processed.ratingAverage = rawRating != null ? Number(rawRating) : undefined
  }

  if (rogue.site.code === 'GBR') {
    const gbpPrice = Number(data.price_gbp || 0)
    if (gbpPrice) {
      processed.price = gbpPrice
      const gbpSalePrice = Number(data.special_price_gbp || 0)
      if (gbpSalePrice && gbpSalePrice < gbpPrice) {
        processed.special_price = gbpSalePrice
      }
    }
  }

  if (data.rogue_rfk_custom_data) {
    try {
      const customData = Object.freeze(JSON.parse(data.rogue_rfk_custom_data)) as {
        rogue_related_label_name?: string;
        rogue_related?: {
          id: string;
          variationKey?: string;
          url?: string;
          sku: string;
          name: string;
          img: string;
          thumbnail: string;
          hover_image?: string;
          color?: string;
          plp_attribute?: string;
          price?: string;
          price_gbp?: string;
          special_price?: string;
          special_price_gbp?: string;
        }[];
        price_gbp?: string;
        special_price_gbp?: string;
        product_has_redirect?: boolean;
        default_bundle_variation_key?: string;
        hide_price?: boolean;
      }

      if (customData.rogue_related?.length) {
        const isSamePage = customData.rogue_related.every(item => Boolean(item.plp_attribute))

        processed.related = customData.rogue_related.map(item => ({
          productId     : isSamePage ? processed.productId : parseInt(item.id, 10),
          childId       : parseInt(item.id, 10),
          variationKey  : item.variationKey,
          color         : item.color,
          related       : extractThumbnailPath(item.img),
          thumbnail     : extractThumbnailPath(item.thumbnail),
          altImage      : item.hover_image ? extractThumbnailPath(item.hover_image) : undefined,
          // Strip query from processed.url before appending sku — it already carries ?sku= for the default child.
          url           : (item.plp_attribute && item.url?.indexOf('?b=') === -1 ? `${processed.url.split('?')[0]}?sku=${item.sku}` : toRelativeProductUrl(item.url || '')),
          name          : item.name,
          price         : Number(rogue.site.code === 'GBR' ? item.price_gbp : item.price) || undefined,
          special_price : Number(rogue.site.code === 'GBR' ? item.special_price_gbp : item.special_price) || undefined,
        })).filter(obj => obj.thumbnail !== processed.thumbnail)
      }

      processed.relatedLabelName = customData.rogue_related_label_name || 'colors'
      processed.hidePrice = customData.hide_price
      processed.hasRedirect = customData.product_has_redirect

      if (!processed.variationKey && customData.default_bundle_variation_key) {
        processed.variationKey = customData.default_bundle_variation_key
      }

      if (rogue.site.code === 'GBR') {
        if (customData.price_gbp) {
          processed.price = Number(customData.price_gbp)
        }
        if (customData.special_price_gbp) {
          const specialGbp = Number(customData.special_price_gbp)
          if (specialGbp < processed.price) {
            processed.special_price = specialGbp
          }
        }
      }
    } catch (e) {
      rogue.log.error('[cnstrc] Error JSON.parse-ing `rogue_rfk_custom_data`', e as Error)
    }
  }

  return processed
}

// ─── Refined content (content rules) ──────────────────────────────────────────
//
// A content rule's payload is an arbitrary JSON object — Constructor's own API schema
// documents `rule.data` as an untyped object. In practice the dashboard writes its
// built-in fields under fixed camelCase keys (verified against a live rule) and flattens
// the merchandiser's tag pairs into the same object. `raw` keeps the untouched payload so
// a rule can carry fields we haven't mapped.
//
// Our tag names deliberately follow the same camelCase convention as these built-ins.

const CONTENT_FIELDS = {
  assetUrl       : 'assetUrl',
  mobileAssetUrl : 'mobileAssetUrl', // assumed; the built-ins above are confirmed
  altText        : 'altText',
  mobileAltText  : 'mobileAltText', // assumed
  header         : 'header',
  body           : 'body',
  html           : 'html',
  ctaText        : 'ctaText',
  ctaUrl         : 'ctaLink',
} as const

// Anything in `data` that isn't one of the built-in fields is a merchandiser tag.
const NON_TAG_KEYS = new Set([
  ...Object.values(CONTENT_FIELDS).map(key => key.toLowerCase()),
  'id',
  'tags',
])

function pickContentField(data: Record<string, any>, key: string): string | undefined {
  const value = data[key]

  return typeof value === 'string' && value.trim() ? value.trim() : undefined
}

// Tags are free-form key/value pairs. The dashboard shows them as pairs, but the payload
// can reach us as an object, an array of {name,value} objects, or an array of "key:value"
// strings depending on how the rule was authored — accept all three, lowercase the keys.
function normalizeContentTags(raw: unknown): Record<string, string> {
  const tags: Record<string, string> = {}

  const add = (key: unknown, value: unknown) => {
    if (typeof key !== 'string' || !key.trim()) return
    if (value == null || typeof value === 'object') return
    tags[key.trim().toLowerCase()] = String(value).trim()
  }

  if (Array.isArray(raw)) {
    for (const entry of raw) {
      if (typeof entry === 'string') {
        const separator = entry.indexOf(':')
        if (separator > 0) add(entry.slice(0, separator), entry.slice(separator + 1))
      } else if (entry && typeof entry === 'object') {
        add((entry as any).name ?? (entry as any).key, (entry as any).value)
      }
    }
  } else if (raw && typeof raw === 'object') {
    for (const [key, value] of Object.entries(raw as Record<string, unknown>)) add(key, value)
  }

  return tags
}

// Gathers a rule's tags from the flat `data` keys the dashboard produces, then merges any
// nested `tags` object on top — API-authored rules and campaigns can send that shape.
function collectContentTags(data: Record<string, any>): Record<string, string> {
  const flat = Object.fromEntries(
    Object.entries(data).filter(([key]) => !NON_TAG_KEYS.has(key.toLowerCase()))
  )

  return { ...normalizeContentTags(flat), ...normalizeContentTags(data.tags) }
}

// Tag names are already lowercased when collected; do the same for the fixed-vocabulary
// values so `Grid`, `New` and `Left` behave like their lowercase spellings.
function tagValue(value: string | undefined): string {
  return (value || '').toLowerCase()
}

function toPlacement(value: string | undefined): CnstrcContentPlacement {
  switch (tagValue(value)) {
    case 'grid':
      return 'grid'
    case 'bottom':
      return 'bottom'
    // A rule with no placement tag is a top-of-results banner — the behavior merchandisers
    // get today without any tagging.
    default:
      return 'banner'
  }
}

// The RogueButton colors that suit a merchandiser CTA. The payment-brand variants
// (klarna-pink, affirm-blue, …) and the link-* styles, which render as text rather than a
// button, are deliberately not offered.
const BUTTON_COLORS = new Set<CnstrcContentSlot['buttonColor']>([
  'red', 'black', 'white', 'gray', 'green', 'black-outline', 'white-outline',
])

function toButtonColor(value: string | undefined): CnstrcContentSlot['buttonColor'] {
  const color = tagValue(value) as CnstrcContentSlot['buttonColor']

  return BUTTON_COLORS.has(color) ? color : 'red'
}

function toImagePosition(value: string | undefined): CnstrcContentSlot['imagePosition'] {
  const position = tagValue(value)

  return position === 'left' || position === 'right' ? position : 'top'
}

function toBoundedInt(value: string | undefined, fallback: number, min: number, max: number): number {
  const parsed = parseInt(value ?? '', 10)
  if (Number.isNaN(parsed)) return fallback

  return Math.min(Math.max(parsed, min), max)
}

// The `site` tag lets one index serve several storefronts (Constructor's own documented
// use case). A rule without the tag shows everywhere; with it, it shows only where the
// comma-separated list names this site's code or country.
function matchesSite(tags: Record<string, string>, rogue: IRogue): boolean {
  const scope = tags.site
  if (!scope) return true

  const allowed = scope.split(',').map(value => value.trim().toLowerCase()).filter(Boolean)
  if (!allowed.length) return true

  return allowed.includes(String(rogue.site.code).toLowerCase())
    || allowed.includes(String(rogue.site.defaultCountry).toLowerCase())
}

// Merchandisers paste fully-qualified CTA URLs. Strip our own host so rogue-link can apply
// the locale prefix; links to any other host are left alone.
function toRelativeCtaUrl(url: string | undefined, rogue: IRogue): string | undefined {
  if (!url || !url.startsWith('http')) return url

  try {
    const { host, pathname, search, hash } = new URL(url)
    if (host.replace(/^www\./, '') !== rogue.site.hostname.replace(/^www\./, '')) return url

    return `${pathname}${search}${hash}`
  } catch {
    return url
  }
}

export function processRefinedContent(refinedContent: unknown, rogue: IRogue): CnstrcContentSlot[] {
  if (!Array.isArray(refinedContent) || !refinedContent.length) return []

  return refinedContent
    .map((entry, index): CnstrcContentSlot | null => {
      // Rules arrive wrapped as { data: {...} }; tolerate a bare object too.
      const data = (entry?.data && typeof entry.data === 'object' ? entry.data : entry) as Record<string, any> | undefined
      if (!data || typeof data !== 'object') return null

      const tags = collectContentTags(data)

      if (!matchesSite(tags, rogue)) return null

      const fields = Object.fromEntries(
        Object.entries(CONTENT_FIELDS).map(([field, key]) => [field, pickContentField(data, key)])
      ) as Record<keyof typeof CONTENT_FIELDS, string | undefined>

      // Nothing to render — an asset, copy, or raw markup is the minimum.
      if (!fields.assetUrl && !fields.header && !fields.body && !fields.html) return null

      return {
        id              : String(entry?.id ?? data.id ?? `cnstrc-content-${index}`),
        ...fields,
        ctaUrl          : toRelativeCtaUrl(fields.ctaUrl, rogue),
        tags,
        // Tag names are matched case-insensitively (normalizeContentTags lowercases them),
        // so the camelCase names merchandisers are given resolve to these lowercase keys.
        placement       : toPlacement(tags.placement),
        resultsPosition : toBoundedInt(tags.position, 1, 1, 1000),
        gridSpan        : toBoundedInt(tags.span, 1, 1, 4),
        imagePosition   : toImagePosition(tags.imageposition),
        buttonColor     : toButtonColor(tags.buttoncolor),
        // Anything other than an explicit `new` keeps the shopper on the page — an
        // unrecognized value should never surprise them with a popup.
        newTab          : tagValue(tags.target) === 'new',
        raw             : data,
      }
    })
    .filter((slot): slot is CnstrcContentSlot => slot !== null)
}

// Returns the Constructor.io item-id string for a buy request, mirroring the rhpa cnstrcItemId logic:
// - Pure grouped (no bundle child) → undefined (item-id belongs on each child row)
// - Single-child grouped (e.g. Jammer) → groupedProductId from metadata
// - All other types → product's own ID
export function getCnstrcItemId(
  buyRequest: BuyRequest,
  products: Record<number, Product>
): string | undefined {
  const product = products[buyRequest.productId]
  if (!product) return undefined
  const bundleChild = buyRequest.children?.find(c => products[c.productId]?.type === 'bundle')
  if (product.type === 'grouped' && !bundleChild) return undefined

  return String(buyRequest.metadata?.groupedProductId ?? product.productId)
}

// Returns the canonical representative child for the selected plp_variation_attribute value.
// Prefers rogue_configurable_default if it matches the color, else first match. Null if unset.
export function resolvePlpRepresentative(colorConfig: Product, selectedSimple: Product, productMap: Record<number, Product>): Product | null {
  const plpAttr = colorConfig.attributes?.plp_variation_attribute as string | undefined

  if (!plpAttr) return null

  const selectedValue = selectedSimple.attributes?.[plpAttr]

  if (selectedValue == null) return null

  const want = String(selectedValue)
  const defaultId = colorConfig.attributes?.rogue_configurable_default

  const matches = (colorConfig.childrenIds || [])
    .map(id => productMap[id])
    .filter((c): c is Product => !!c && String(c.attributes?.[plpAttr]) === want)

  return matches.find(c => `${c.productId}` === `${defaultId}` || c.sku === defaultId) || matches[0] || null
}

// Builds the Constructor variation-id string for a bundle (e.g. "1130914-1139452-3698-...").
// First segment: color representative from option 0 via plp_variation_attribute.
// Remaining segments: ?b= URL param slice 1+ when present; otherwise live bundle options qty > 0.
export function buildBundleVariationId(
  selectedSimple: Product,
  bundleBuyRequest: BuyRequest,
  bundleProduct: any,
  bundleParams: string | undefined,
  productMap: Record<number, Product>,
  rogue: IRogue
): string | undefined {
  if (!bundleProduct?.bundleOptions?.length) return undefined

  const rackBR = bundleBuyRequest.children?.find((c: any) =>
    c.metadata?.bundleOptionId === bundleProduct.bundleOptions[0].id
  )

  const colorConfig = rackBR ? productMap[rackBR.productId] : null

  let colorId: number = selectedSimple.productId

  if (colorConfig) {
    const rep = resolvePlpRepresentative(colorConfig, selectedSimple, productMap)
    
    if (rep) colorId = rep.productId
  }

  const accessoryIds = bundleParams
    ? bundleParams.split('-').slice(1)
    : (bundleProduct.bundleOptions as any[])
      .slice(1)
      .map((opt: any) => bundleBuyRequest.children?.find((c: any) => c.metadata?.bundleOptionId === opt.id))
      .filter((c: any) => c?.qty !== 0)
      .map((c: any) => getSimpleForBuyRequest(c, rogue)?.productId)
      .filter(Boolean)

  return [colorId, ...accessoryIds].join('-')
}
